// La cifra de sellers vive en un solo sitio: ver la nota en marketing-stats.ts.
import { SELLER_BASE } from "./marketing-stats";

export interface PromiseCard {
  icon: string;
  title: string;
  textPre: string;
  strong: string;
  textPost: string;
}

export type HeroSubSegment =
  | { kind: "text"; value: string }
  | { kind: "strong"; value: string };

export interface HeroVariantCopy {
  eyebrow: string;
  headlineLines: Array<Array<{ kind: "text" | "strike" | "accent"; value: string }>>;
  sub: HeroSubSegment[];
  ctaPrimary: string;
  ctaGhost: string;
}

export const FOOTER_TAGLINE =
  "Made for operators who are tired of buying the same list twice.";

export const VERIFICATION_PULL_QUOTE = {
  eyebrow: "/ The verification stack",
  text: "The most complex research stack in the category. The most solid verification.",
} as const;

export const INLINE_METRICS = [
  `${SELLER_BASE.compact} sellers`,
  "98% accuracy",
  "30-day freshness",
  "72h delivery",
] as const;

export const MARKETING_COPY = {
  // ============================================================
  // HERO VARIANTS. Each page gets its own incipit/personality.
  // ============================================================
  heroHome: {
    eyebrow: "/ The new seller intelligence layer",
    headlineLines: [
      [
        { kind: "text", value: "We don't sell " },
        { kind: "strike", value: "lists." },
      ],
      [
        { kind: "text", value: "We " },
        { kind: "accent", value: "find your buyers." },
      ],
    ],
    sub: [
      { kind: "strong", value: "Custom research on every order." },
      { kind: "text", value: " Re-verified in the last 30 days. Matched to " },
      { kind: "strong", value: "your" },
      { kind: "text", value: " ICP, not someone else's." },
    ],
    ctaPrimary: `Search ${SELLER_BASE.compact} sellers free`,
    ctaGhost: "See how we research",
  } satisfies HeroVariantCopy,

  heroWhy: {
    eyebrow: "/ The case against recycled CSVs",
    headlineLines: [
      [{ kind: "text", value: "Lists are recycled." }],
      [{ kind: "text", value: "Buyers are not." }],
    ],
    sub: [
      {
        kind: "text",
        value:
          "Every other vendor hands you a database and wishes you luck. We start from your ",
      },
      { kind: "strong", value: "pain point" },
      {
        kind: "text",
        value:
          ". We look at who converts, who doesn't, and why, then research the sellers that fit. Custom per brief. Verified in the last 30 days.",
      },
    ],
    ctaPrimary: "See a sample delivery",
    ctaGhost: "Talk to research team",
  } satisfies HeroVariantCopy,

  heroDiscovery: {
    eyebrow: `/ ${SELLER_BASE.compact} sellers · re-verified monthly`,
    headlineLines: [
      [
        { kind: "text", value: "Find the " },
        { kind: "accent", value: "exact sellers" },
      ],
      [{ kind: "text", value: "you should be talking to." }],
    ],
    sub: [
      {
        kind: "text",
        value:
          "Filter by category, marketplace, revenue band, freshness, and ICP fit. Export only what's verified in the last 30 days.",
      },
    ],
    ctaPrimary: "",
    ctaGhost: "",
  } satisfies HeroVariantCopy,

  // ============================================================
  // SHARED SECTIONS
  // ============================================================
  contrast: {
    headLeft: "The rest",
    headRight: "LeadSweet",
    rows: [
      {
        label: "Starting point",
        them: "Generic database, you filter down",
        // Antes: "Your pain point, we research up" (LeadSweet Site - Notes.docx)
        us: "Your pain point, we research, and custom-build a solution",
      },
      {
        label: "Freshness",
        them: "12 to 18 month old contacts",
        us: "Re-verified in the last 30 days",
      },
      {
        label: "Targeting",
        them: "Generic Amazon seller dumps",
        us: "Matched to your ICP and niche",
      },
      {
        label: "Verification",
        them: "One scraper, one pass, one source",
        us: "Dozens of trained agents · triple-verified",
      },
    ],
  },

  // The big editorial quote block under the Why Us hero.
  whyQuote: {
    quote: "Lists rot. Research compounds.",
    // Antes: "LeadSweet, on why we don't keep a database"
    // (LeadSweet Site - Notes.docx). El matiz importa: si no guardamos ninguna
    // base de datos, la pagina de discovery no tendria sentido; lo que no
    // guardamos es una base ESTANCADA.
    attribution: "We don't keep a stagnant database",
  },

  manifesto: {
    eyebrow: "/ Manifesto",
    title: "What LeadSweet stands for",
    paragraphs: [
      [
        { kind: "text", value: "The lead-list industry runs on " },
        { kind: "crossed", value: "recycled CSVs" },
        { kind: "text", value: ", " },
        { kind: "crossed", value: "stale contacts" },
        { kind: "text", value: ", and " },
        { kind: "crossed", value: "copy-paste databases" },
        { kind: "text", value: " sold to every agency on the same day." },
      ],
      [
        { kind: "text", value: "We " },
        { kind: "em", value: "refuse" },
        { kind: "text", value: " to operate that way." },
      ],
      [
        { kind: "text", value: "Each LeadSweet brief triggers a " },
        { kind: "strong", value: "fresh research cycle" },
        { kind: "text", value: ". Each seller is " },
        { kind: "strong", value: "re-verified" },
        { kind: "text", value: " within thirty days. Each list is " },
        { kind: "strong", value: "built around your pain point" },
        { kind: "text", value: ", not lifted from a database." },
      ],
      [
        { kind: "text", value: "That is the difference between a list and a " },
        { kind: "em", value: "lead." },
      ],
    ] as Array<Array<{ kind: "text" | "crossed" | "em" | "strong"; value: string }>>,
  },

  // The 4-step process used on Home (legacy HowItWorks, kept for type compat).
  // The Home page now renders the new ProcessSection with its own hard-coded copy.
  how: {
    eyebrow: "/ The process",
    titleLead: "Custom research,",
    titleEm: "every time.",
    sub: "Four steps, seventy-two hours, zero recycled data. Every brief becomes its own project, never pulled from a stockpile.",
    steps: [
      {
        num: "Step 01",
        title: "You define the ICP",
        text: "Marketplace, category, revenue band, brand model, geography. The narrower the brief, the sharper the list.",
      },
      {
        num: "Step 02",
        title: "Dozens of trained agents, triple-verified",
        text: "Your brief runs through dozens of AI agents we trained specifically for seller research. Each one specializes in a single signal: catalog, revenue band, marketplace behavior, or contact freshness. Every result is then cross-checked three times: source, API, and human reviewer.",
      },
      {
        num: "Step 03",
        title: "Every contact re-verified",
        text: "Email valid, phone live, LinkedIn current. If a contact is older than thirty days, we re-check before shipping.",
      },
      {
        num: "Step 04",
        title: "Built around your pain, not their database",
        text: "Most vendors start from a list and let you filter. We start from your problem, who you sell to, what makes them convert, where they hurt, then reverse-engineer the seller list from that brief. You get the sellers that actually fit, not a CSV you have to clean.",
      },
    ],
  },

  // The 3-step "How research works" used on Why Us.
  howResearch: {
    eyebrow: "/ How research works",
    titleLead: "Brief. Research.",
    titleEm: "Verify.",
    sub: "Three movements, seventy-two hours. No database lookups, no recycled rows.",
    steps: [
      {
        num: "01 / Brief",
        title: "You write the ICP",
        text: "Category, marketplace, revenue band, geography. Narrow beats broad. Every constraint sharpens the list.",
      },
      {
        num: "02 / Research",
        title: "Dozens of trained agents, triple-verified",
        text: "Your brief runs through dozens of AI agents we trained specifically for seller research. Each one specializes in a single signal: catalog, revenue band, marketplace behavior, or contact freshness. Every result is then cross-checked three times: source, API, and human reviewer.",
      },
      {
        num: "03 / Verify",
        title: "Every contact, last 30 days",
        text: "Email valid. Phone live. LinkedIn current. Anything older than thirty days is re-checked or dropped.",
      },
    ],
  },

  promises: {
    eyebrow: "/ Our guarantees",
    titleLead: "Six promises",
    titleEm: "nobody else",
    titleTail: " makes.",
    sub: "Hard commitments, written into every contract. Not marketing claims, not asterisks, not fine print.",
    cards: [
      {
        icon: "01",
        title: "Built from your brief, not a database",
        textPre: "We don't ship pre-cut lists. ",
        strong: "Every delivery is reverse-engineered from your ICP",
        textPost: ": the sellers that fit your pain point, not the ones that fit a search query.",
      },
      {
        icon: "02",
        title: "Maximum thirty-day age",
        textPre: "Every email, phone, and LinkedIn URL is re-verified within thirty days of delivery. ",
        strong: "Older contacts are dropped, not shipped.",
        textPost: "",
      },
      {
        icon: "03",
        title: "98% accuracy or we replace",
        textPre: "Hard-bounce rate above two percent on first send? ",
        strong: "We replace every bounced contact at no cost.",
        textPost: "",
      },
      {
        icon: "04",
        title: "Custom research per brief",
        textPre: "No pre-built CSVs sitting on a server. ",
        strong: "Each order triggers a fresh research cycle",
        textPost: " tailored to your ICP.",
      },
      {
        icon: "05",
        title: "Trained agent stack",
        textPre: "Dozens of AI agents specialized for seller research. Each focuses on a single signal: catalog, revenue, marketplace behavior, contact freshness. ",
        strong: "Forty-plus underlying sources, cross-checked, never single-sourced.",
        textPost: "",
      },
      {
        icon: "06",
        title: "Seventy-two hour delivery",
        textPre: "From brief approved to CSV in your inbox in three business days. ",
        strong: "Slower than a stock list. Worth every hour.",
        textPost: "",
      },
    ] satisfies ReadonlyArray<PromiseCard>,
  },

  finalCta: {
    headlineLead: "Stop buying recycled Amazon lists.",
    headlineEm: "Start pitching sellers who need you this week.",
    sub: "Send us your ICP and target signal. We build a free 50-record sample so you can run it through Clay, HeyReach, Lemlist, Smartlead, Instantly, or HubSpot before you decide.",
    cta: "Request a custom sample",
  },
} as const;
